'use client';

import { useEffect, useState } from 'react';
import {
  AlertCircle,
  Clock,
  CheckCircle2,
  Download,
  Eye,
  EyeOff,
  Heart,
  Loader2,
  MessageCircle,
  MoreHorizontal,
  Music,
  RefreshCw,
  Share2,
  Trash2,
  VideoOff,
  Wand2,
  XCircle,
} from 'lucide-react';
import { S3Image } from './s3-image';
import { ImagePreviewModal } from './image-preview-modal';
import { TrendInfoCard } from './trend-info-card';
import { VideoPlayer } from './video-player';
import { cn } from '../utils/cn';

const statusConfig = {
  PENDING: { icon: Clock, color: 'text-white', label: 'Ожидание' },
  PROCESSING: { icon: Loader2, color: 'text-white animate-spin', label: 'Генерация...' },
  COMPLETED: { icon: CheckCircle2, color: 'text-green-500', label: 'Готово' },
  FAILED: { icon: XCircle, color: 'text-red-500', label: 'Ошибка' },
} as const;

export interface HistoryDetailScreenData {
  id: string;
  status: keyof typeof statusConfig | string;
  resultUrl: string | null;
  thumbnailUrl: string | null;
  audioTracks?: Array<{
    id: string;
    audioUrl: string;
    imageUrl?: string | null;
    title?: string | null;
    tags?: string | null;
    duration?: number | null;
    modelName?: string | null;
  }>;
  resultType: 'IMAGE' | 'VIDEO' | 'AUDIO';
  prompt: string | null;
  error?: string | null;
  isPublished?: boolean;
  feedStatus?: string | null;
  isPromptPrivate?: boolean;
  model?: {
    name?: string | null;
  } | null;
  trend?: {
    id: string;
    name: string;
    description?: string | null;
    imageUrl?: string | null;
  } | null;
  creator: {
    name: string;
    photoUrl?: string | null;
    initial: string;
  };
}

type PendingAction = 'profile' | 'feed' | 'privacy' | 'delete' | null;

interface HistoryDetailScreenProps {
  generation?: HistoryDetailScreenData | null;
  isLoading?: boolean;
  hasError?: boolean;
  isNotFound?: boolean;
  isFeedSource?: boolean;
  isOwner?: boolean;
  pendingAction?: PendingAction;
  updatePending?: boolean;
  deletePending?: boolean;
  onRetryLoad: () => void;
  onBack: () => void;
  onGoHome?: () => void;
  onShare: () => void;
  onRepeat: () => void;
  onDownload: () => void;
  onEdit: () => void;
  onAnimate: () => void;
  onTogglePromptPrivacy: () => void;
  onSupportClick: () => void;
  onPublishToProfileAndFeed: () => void;
  onUnpublishFromFeed: () => void;
  onUnpublishFromProfile: () => void;
  onDelete: () => void;
}

export function HistoryDetailScreen({
  generation,
  isLoading = false,
  hasError = false,
  isNotFound = false,
  isFeedSource = false,
  isOwner = false,
  pendingAction = null,
  updatePending = false,
  deletePending = false,
  onRetryLoad,
  onBack,
  onGoHome,
  onShare,
  onRepeat,
  onDownload,
  onEdit,
  onAnimate,
  onTogglePromptPrivacy,
  onSupportClick,
  onPublishToProfileAndFeed,
  onUnpublishFromFeed,
  onUnpublishFromProfile,
  onDelete,
}: HistoryDetailScreenProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [showUnpublishConfirm, setShowUnpublishConfirm] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [mediaLoaded, setMediaLoaded] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  useEffect(() => {
    setShowDeleteConfirm(false);
    setShowMoreMenu(false);
    setShowPublishConfirm(false);
    setShowUnpublishConfirm(false);
    setVideoError(false);
    setMediaLoaded(false);
    setIsPreviewOpen(false);
  }, [generation?.id]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <DetailBackHeader onBack={onBack} />
        <div className="flex flex-1 items-center justify-center pt-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (hasError || !generation) {
    return (
      <div className="min-h-screen bg-background">
        <DetailBackHeader onBack={onBack} />
        <div className="flex flex-col items-center justify-center p-4 pt-16">
          <XCircle className="h-12 w-12 text-red-500" />
          <p className="mt-4 text-center text-muted-foreground">
            {isNotFound ? 'Генерация не найдена' : 'Ошибка загрузки'}
          </p>
          <div className="mt-4 flex flex-col gap-2">
            {!isNotFound && (
              <button
                onClick={onRetryLoad}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
              >
                Попробовать снова
              </button>
            )}
            {onGoHome && (
              <button
                onClick={onGoHome}
                className="rounded-lg bg-white/10 px-4 py-2 text-sm font-medium text-white"
              >
                На главный экран
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const status =
    generation.status in statusConfig
      ? (generation.status as keyof typeof statusConfig)
      : 'COMPLETED';
  const statusInfo = statusConfig[status];
  const StatusIcon = statusInfo.icon;
  const isCompleted = generation.status === 'COMPLETED';
  const isFailed = generation.status === 'FAILED';
  const publishDisabled = !isOwner || (isFeedSource && isOwner);
  const publishLabel =
    (isFeedSource && isOwner) || (generation.isPublished && generation.feedStatus === 'APPROVED')
      ? 'Опубликовано'
      : generation.feedStatus === 'PENDING_MODERATION'
        ? 'На модерации'
        : 'Опубликовать';

  const handlePublishClick = () => {
    if (publishDisabled) return;

    if (generation.isPublished || generation.feedStatus === 'APPROVED' || generation.feedStatus === 'PENDING_MODERATION') {
      setShowUnpublishConfirm(true);
      return;
    }

    setShowPublishConfirm(true);
  };

  return (
    <div className="min-h-screen bg-background pb-20">
      <div className="relative z-10 flex h-[50px] items-center justify-between bg-background px-4">
        <button
          onClick={onBack}
          className="detail-back-button flex h-9 w-9 items-center justify-center rounded-full bg-[#242424]/40"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="#fafafa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 4L5 10L11 16" />
            <line x1="5" y1="10" x2="16" y2="10" />
          </svg>
        </button>

        <div className="flex items-center gap-2.5">
          <button className="flex h-10 w-10 items-center justify-center rounded-full bg-[#242424]/40">
            <Heart
              className={cn(
                'h-5 w-5',
                generation.isPublished ? 'fill-red-500 text-red-500' : 'text-white',
              )}
            />
          </button>
          <button
            onClick={onShare}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-[#242424]/40"
          >
            <Share2 className="h-5 w-5 text-white" />
          </button>
        </div>
      </div>

      <div className={cn('relative w-full bg-background', !mediaLoaded && 'min-h-[300px]')}>
        {(!isCompleted ||
          (isCompleted &&
            generation.resultUrl &&
            generation.resultType === 'VIDEO' &&
            !mediaLoaded &&
            !videoError)) && (
          <div className="pointer-events-none absolute inset-0 z-[5] flex flex-col items-center justify-center gap-4">
            {isCompleted ? (
              <Loader2 className="h-16 w-16 animate-spin text-white" />
            ) : (
              <StatusIcon className={cn('h-16 w-16', statusInfo.color)} />
            )}
            <p className="text-sm text-[#8e8e93]">
              {isCompleted ? 'Загрузка...' : statusInfo.label}
            </p>
          </div>
        )}

        {isCompleted && generation.resultUrl && (
          <>
            {generation.resultType === 'VIDEO' ? (
              videoError ? (
                <div className="flex min-h-[200px] flex-col items-center justify-center gap-2 text-[#8e8e93]">
                  <VideoOff className="h-8 w-8" />
                  <span className="text-xs">Не удалось загрузить видео</span>
                </div>
              ) : (
                <VideoPlayer
                  src={generation.resultUrl}
                  poster={generation.thumbnailUrl}
                  preload="metadata"
                  className="w-full"
                  videoClassName="w-full object-contain"
                  onLoadedMetadata={() => setMediaLoaded(true)}
                  onLoadedData={() => setMediaLoaded(true)}
                  onCanPlay={() => setMediaLoaded(true)}
                  onError={() => {
                    setVideoError(true);
                    setMediaLoaded(true);
                  }}
                />
              )
            ) : generation.resultType === 'AUDIO' ? (
              <AudioDetailPlayer
                tracks={
                  generation.audioTracks && generation.audioTracks.length > 0
                    ? generation.audioTracks
                    : [{
                        id: generation.id,
                        audioUrl: generation.resultUrl,
                        imageUrl: generation.thumbnailUrl,
                        title: generation.model?.name || null,
                      }]
                }
                thumbnailUrl={generation.thumbnailUrl}
                prompt={generation.prompt}
              />
            ) : (
              <div
                className="relative w-full cursor-pointer"
                onClick={() => setIsPreviewOpen(true)}
              >
                <S3Image
                  src={generation.resultUrl}
                  alt={generation.prompt || 'Generated image'}
                  width={588}
                  height={588}
                  className="h-auto w-full"
                  priority
                  showSkeleton
                  onLoad={() => setMediaLoaded(true)}
                  onError={() => setMediaLoaded(true)}
                />
              </div>
            )}
          </>
        )}

        {generation.feedStatus === 'APPROVED' && (
          <span className="absolute right-0 top-0 flex items-center gap-1 rounded-bl-[10px] bg-[#9dd94a] px-1.5 pb-1 pt-0.5 text-[11px] font-bold text-white">
            В ленте
            <svg width="18" height="18" viewBox="0 0 21 21" fill="none" className="shrink-0" style={{ width: 18, height: 18 }}>
              <circle cx="10.5" cy="10.5" r="9" stroke="#fff" strokeWidth="1.3" />
              <path d="M6.44 10.46l2.72 2.72 5.44-5.44" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        )}
        {generation.feedStatus === 'PENDING_MODERATION' && (
          <span className="absolute right-0 top-0 rounded-bl-[10px] bg-[#25272a] px-2 pb-1 pt-0.5 text-[11px] font-medium text-white">
            На модерации
          </span>
        )}
      </div>

      {isCompleted && (
        <div className="px-4 pt-4">
          <button
            onClick={onRepeat}
            className="flex h-14 w-full items-center justify-center rounded-[20px]"
            style={{
              background: 'linear-gradient(180deg, #ef6444 0%, #ff7758 100%)',
              border: '1px solid rgba(255,255,255,0.12)',
              boxShadow:
                '-1px 3px 5.25px rgba(0,0,0,0.08), inset 0 1px 0 0 rgba(255,255,255,0.15)',
            }}
          >
            <span className="font-benzin text-base font-semibold text-white">
              ПОВТОРИТЬ
            </span>
          </button>
        </div>
      )}

      {isCompleted && (
        <div className="space-y-2.5 px-4 pt-4">
          <div className="grid grid-cols-2 gap-2.5">
            {(generation.resultType === 'VIDEO'
              || generation.resultType === 'AUDIO'
              ? [
                  // Media results without image input semantics cannot be edited/animated from this screen.
                  { icon: Download, label: 'Скачать', onClick: onDownload, disabled: false },
                  { icon: Share2, label: publishLabel, onClick: handlePublishClick, disabled: publishDisabled },
                ]
              : [
                  { icon: Download, label: 'Скачать', onClick: onDownload, disabled: false },
                  { icon: Wand2, label: 'Изменить', onClick: onEdit, disabled: false },
                  { icon: RefreshCw, label: 'Анимировать', onClick: onAnimate, disabled: false },
                  { icon: Share2, label: publishLabel, onClick: handlePublishClick, disabled: publishDisabled },
                ]
            ).map((button) => (
              <button
                key={button.label}
                onClick={button.disabled ? undefined : button.onClick}
                className={cn(
                  'flex h-[87px] flex-col items-center justify-center rounded-[20px]',
                  button.disabled && 'pointer-events-none opacity-30',
                )}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(22px)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  boxShadow:
                    '-1px 3px 5.25px rgba(0,0,0,0.08), inset 0 1px 0 0 rgba(255,255,255,0.15), inset 0 -1px 0 0 rgba(255,255,255,0.03)',
                }}
              >
                <button.icon className="mb-2 h-6 w-6 text-white" strokeWidth={1.5} />
                <span
                  className="text-sm font-semibold text-[#e7e8ea]"
                  style={{ fontFamily: 'Open Sans, Inter, sans-serif' }}
                >
                  {button.label}
                </span>
              </button>
            ))}
          </div>

          <button
            onClick={isOwner ? () => setShowMoreMenu((value) => !value) : undefined}
            className={cn(
              'flex h-[48px] w-full items-center justify-center rounded-[20px]',
              !isOwner && 'pointer-events-none opacity-30',
            )}
            style={{
              background: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.12)',
              boxShadow: 'inset 0 1px 0 0 rgba(255,255,255,0.15)',
            }}
          >
            <MoreHorizontal className="mr-2 h-5 w-5 text-[#e7e8ea]" strokeWidth={1.5} />
            <span
              className="text-sm font-semibold text-[#e7e8ea]"
              style={{ fontFamily: 'Open Sans, Inter, sans-serif' }}
            >
              Ещё
            </span>
          </button>
        </div>
      )}

      {isFailed && isOwner && (
        <div className="space-y-3 px-4 pt-4">
          {generation.error && (
            <div className="rounded-[20px] border border-red-500/20 bg-red-500/10 px-5 py-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-400" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-red-300">
                    Не удалось выполнить генерацию
                  </p>
                  <p className="mt-1 text-sm leading-relaxed text-[#e7e8ea]">
                    {generation.error}
                  </p>
                </div>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2.5">
            <button
              onClick={onRepeat}
              className="flex items-center gap-3 rounded-[20px] bg-white/10 px-5 py-4"
            >
              <RefreshCw className="h-5 w-5 text-white" />
              <span className="text-sm font-medium text-white">Повторить</span>
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center gap-3 rounded-[20px] bg-red-500/10 px-5 py-4"
            >
              <Trash2 className="h-5 w-5 text-red-500" />
              <span className="text-sm font-medium text-red-500">Удалить</span>
            </button>
          </div>
          <button
            onClick={onSupportClick}
            className="flex w-full items-center justify-center gap-2 rounded-[20px] bg-white/10 px-5 py-4 text-sm text-white"
          >
            <MessageCircle className="h-5 w-5" />
            Написать в техподдержку
          </button>
        </div>
      )}

      {showMoreMenu && isOwner && (
        <div className="space-y-2 px-4 pt-2">
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="flex w-full items-center gap-3 rounded-[14px] bg-red-500/10 px-4 py-3"
          >
            <Trash2 className="h-5 w-5 text-red-500" />
            <span className="text-sm font-medium text-red-500">Удалить</span>
          </button>
        </div>
      )}

      <div className="px-4 pt-4">
        <div className="overflow-hidden rounded-xl bg-white/5">
          <div className="flex items-center justify-between px-4 py-3">
            <div>
              <p className="text-xs text-[#8e8e93]">Создатель</p>
              <p className="mt-0.5 text-sm font-semibold text-[#e7e8ea]">
                {generation.creator.name}
              </p>
            </div>
            <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full border border-accent bg-[#4e4e50]">
              {generation.creator.photoUrl ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={generation.creator.photoUrl}
                  alt=""
                  className="h-full w-full object-cover"
                />
              ) : (
                <span className="text-sm font-bold text-white">
                  {generation.creator.initial}
                </span>
              )}
            </div>
          </div>

          <div className="mx-4 h-px bg-[#4e4e50]/50" />

          {generation.trend ? (
            <div className="px-4 py-3">
              <p className="mb-2 text-xs text-[#8e8e93]">Тренд</p>
              <TrendInfoCard
                trendId={generation.trend.id}
                name={generation.trend.name}
                description={generation.trend.description}
                imageUrl={generation.trend.imageUrl}
                clickable
              />
            </div>
          ) : generation.prompt ? (
            <div className="px-4 py-3">
              <div className="flex items-center justify-between">
                <p className="text-xs text-[#8e8e93]">Промпт</p>
                {isCompleted && isOwner && (
                  <button
                    onClick={onTogglePromptPrivacy}
                    disabled={updatePending}
                    className="flex items-center gap-1 text-xs text-[#8e8e93]"
                  >
                    {pendingAction === 'privacy' ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : generation.isPromptPrivate ? (
                      <>
                        <EyeOff className="h-3 w-3" /> Скрыт
                      </>
                    ) : (
                      <>
                        <Eye className="h-3 w-3" /> Виден
                      </>
                    )}
                  </button>
                )}
              </div>
              <p className="mt-1 text-sm leading-relaxed text-[#e7e8ea]">
                {generation.prompt}
              </p>
            </div>
          ) : null}

          <div className="mx-4 h-px bg-[#4e4e50]/50" />

          <div className="flex items-center justify-between px-4 py-3">
            <p className="text-xs text-[#8e8e93]">Модель</p>
            <p className="text-sm font-semibold text-[#e7e8ea]">
              {generation.model?.name || 'Неизвестно'}
            </p>
          </div>
        </div>
      </div>

      {showUnpublishConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-background p-6">
            <h3 className="text-lg font-semibold text-white">Снять публикацию</h3>
            <p className="mt-3 text-sm text-[#8e8e93]">Выберите откуда убрать фото:</p>
            <div className="mt-4 space-y-2">
              {(generation.feedStatus === 'APPROVED' ||
                generation.feedStatus === 'PENDING_MODERATION') && (
                <button
                  onClick={onUnpublishFromFeed}
                  disabled={updatePending}
                  className="w-full rounded-xl bg-white/10 py-3 text-sm font-medium text-white"
                >
                  Убрать из ленты
                </button>
              )}
              {generation.isPublished && (
                <button
                  onClick={onUnpublishFromProfile}
                  disabled={updatePending}
                  className="w-full rounded-xl bg-white/10 py-3 text-sm font-medium text-white"
                >
                  Убрать из профиля
                </button>
              )}
              <button
                onClick={() => setShowUnpublishConfirm(false)}
                className="w-full rounded-xl bg-white/5 py-3 text-sm font-medium text-[#8e8e93]"
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}

      {showPublishConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-background p-6">
            <h3 className="text-lg font-semibold text-white">Опубликовать</h3>
            <p className="mt-3 text-sm text-[#8e8e93]">
              Фото будет добавлено в ваш профиль и отправлено на модерацию для публикации в общей ленте.
            </p>
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setShowPublishConfirm(false)}
                className="flex-1 rounded-xl bg-white/10 py-3 text-sm font-medium text-white"
              >
                Отмена
              </button>
              <button
                onClick={onPublishToProfileAndFeed}
                disabled={updatePending}
                className="flex-1 rounded-xl bg-primary py-3 text-sm font-medium text-primary-foreground"
              >
                {pendingAction === 'feed' ? (
                  <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                ) : (
                  'Опубликовать'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-background p-6">
            <div className="flex items-center gap-3 text-red-500">
              <Trash2 className="h-6 w-6" />
              <h3 className="text-lg font-semibold text-white">
                Удалить генерацию?
              </h3>
            </div>
            <p className="mt-4 text-sm text-[#8e8e93]">
              Изображение или видео будет удалено безвозвратно. Это действие нельзя отменить.
            </p>
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 rounded-xl bg-white/10 py-3 text-sm font-medium text-white"
              >
                Отмена
              </button>
              <button
                onClick={onDelete}
                disabled={deletePending}
                className="flex-1 rounded-xl bg-red-500 py-3 text-sm font-medium text-white"
              >
                {pendingAction === 'delete' ? (
                  <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                ) : (
                  'Удалить'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {generation.resultUrl && generation.resultType === 'IMAGE' && (
        <ImagePreviewModal
          isOpen={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
          src={generation.resultUrl}
          alt={generation.prompt || 'Generated'}
        />
      )}
    </div>
  );
}

function AudioDetailPlayer({
  tracks,
  thumbnailUrl,
  prompt,
}: {
  tracks: Array<{
    id: string;
    audioUrl: string;
    imageUrl?: string | null;
    title?: string | null;
    tags?: string | null;
    duration?: number | null;
    modelName?: string | null;
  }>;
  thumbnailUrl?: string | null;
  prompt?: string | null;
}) {
  const primaryTrack = tracks[0];
  const coverUrl = primaryTrack?.imageUrl || thumbnailUrl;

  return (
    <div className="relative flex min-h-[360px] w-full flex-col items-center justify-end overflow-hidden bg-[#17181a] px-5 pb-6 pt-16">
      {coverUrl ? (
        <S3Image
          src={coverUrl}
          alt={prompt || 'Music cover'}
          width={588}
          height={588}
          className="absolute inset-0 h-full w-full object-cover opacity-60 blur-sm scale-105"
          priority
        />
      ) : null}
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/35 to-black/80" />
      <div className="relative z-[1] mb-6 flex h-28 w-28 items-center justify-center overflow-hidden rounded-[28px] bg-white/10 shadow-2xl">
        {coverUrl ? (
          <S3Image
            src={coverUrl}
            alt={prompt || 'Music cover'}
            width={160}
            height={160}
            className="h-full w-full object-cover"
            priority
          />
        ) : (
          <Music className="h-12 w-12 text-white" strokeWidth={1.5} />
        )}
      </div>
      {prompt ? (
        <p className="relative z-[1] mb-4 line-clamp-3 max-w-[90%] text-center text-sm font-medium leading-5 text-white">
          {prompt}
        </p>
      ) : null}
      <div className="relative z-[1] flex w-full flex-col gap-3">
        {tracks.map((track, index) => (
          <div
            key={track.id || `${track.audioUrl}-${index}`}
            className="rounded-[18px] border border-white/10 bg-black/30 p-3 backdrop-blur"
          >
            <div className="mb-2 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-white">
                  {track.title || `Вариант ${index + 1}`}
                </p>
                {track.tags ? (
                  <p className="truncate text-xs text-white/60">{track.tags}</p>
                ) : null}
              </div>
              {typeof track.duration === 'number' ? (
                <span className="shrink-0 text-xs font-medium text-white/70">
                  {formatAudioDuration(track.duration)}
                </span>
              ) : null}
            </div>
            <audio className="w-full" controls preload="metadata" src={track.audioUrl} />
          </div>
        ))}
      </div>
    </div>
  );
}

function formatAudioDuration(duration: number): string {
  const totalSeconds = Math.max(0, Math.round(duration));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function DetailBackHeader({ onBack }: { onBack: () => void }) {
  return (
    <div className="relative z-10 flex h-[50px] items-center justify-between bg-background px-4">
      <button
        onClick={onBack}
        className="detail-back-button flex h-9 w-9 items-center justify-center rounded-full bg-[#242424]/40"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          stroke="#fafafa"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M11 4L5 10L11 16" />
          <line x1="5" y1="10" x2="16" y2="10" />
        </svg>
      </button>
    </div>
  );
}
